import { useMemo } from "react";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import type { FlatRowSelectOption } from "web_myplann/Shared/Components/FlatRow/FlatRowSelect";

type EnumValue = string | number;

type LabelMap<TValue extends EnumValue> = Record<TValue, string>;

export const useEnumSelectOptions = <
	TEnum extends Record<string, EnumValue>,
	const TExcluded extends TEnum[keyof TEnum] = never,
>(
	enumObject: TEnum,
	labelResources: LabelMap<Exclude<TEnum[keyof TEnum], TExcluded>>,
	excludeValues: readonly TExcluded[] = [],
): FlatRowSelectOption<Exclude<TEnum[keyof TEnum], TExcluded>>[] => {
	const { t } = useResource();
	const excludeKey = excludeValues.join("|");

	return useMemo(
		() =>
			(Object.values(enumObject) as TEnum[keyof TEnum][])
				.filter(
					(value): value is Exclude<TEnum[keyof TEnum], TExcluded> =>
						!excludeValues.includes(value as TExcluded),
				)
				.map((value) => ({
					value,
					label: t(labelResources[value]),
				})),
		[enumObject, labelResources, t, excludeKey],
	);
};
